import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryImages } from '../data/galleryData';

const ImageModal = ({ 
  showImageModal, 
  currentImageIndex, 
  setCurrentImageIndex, 
  closeImageModal 
}) => {
  const navigateImage = useCallback((direction) => {
    if (direction === 'next') {
      setCurrentImageIndex((prev) => 
        prev === galleryImages.length - 1 ? 0 : prev + 1
      );
    } else {
      setCurrentImageIndex((prev) => 
        prev === 0 ? galleryImages.length - 1 : prev - 1
      );
    }
  }, [setCurrentImageIndex]);

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!showImageModal) return;
      
      if (e.key === 'Escape') {
        closeImageModal();
      } else if (e.key === 'ArrowRight') {
        navigateImage('next');
      } else if (e.key === 'ArrowLeft') {
        navigateImage('prev');
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [showImageModal, closeImageModal, navigateImage]);

  if (!showImageModal) return null;

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-hidden">
      {/* Close button */}
      <button
        onClick={closeImageModal}
        className="absolute top-6 right-6 z-50 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 text-white transition-colors duration-200"
      >
        <X size={24} />
      </button>

      {/* Navigation buttons */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigateImage('prev');
        }}
        className="absolute left-6 top-1/2 -translate-y-1/2 z-50 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 text-white transition-colors duration-200"
      >
        <ChevronLeft size={24} />
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          navigateImage('next');
        }}
        className="absolute right-6 top-1/2 -translate-y-1/2 z-50 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 text-white transition-colors duration-200"
      >
        <ChevronRight size={24} />
      </button>

      {/* Main image container */}
      <div className="relative w-full h-full flex flex-col items-center justify-center px-16 py-20 md:px-20 md:py-24">
        <div className="relative max-w-full max-h-full flex items-center justify-center mb-6">
          <img
            src={galleryImages[currentImageIndex].src}
            alt={galleryImages[currentImageIndex].alt}
            className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-2xl"
            style={{ maxWidth: '100%', maxHeight: 'calc(100% - 100px)' }}
          />
        </div>
        
        {/* Minimalist image info below image */}
        <div className="text-center text-white max-w-2xl px-4">
          <div className="flex items-center justify-center gap-4 mb-2">
            <span className="text-xs font-medium text-red-400 uppercase tracking-wider">
              {galleryImages[currentImageIndex].category}
            </span>
            <span className="text-xs text-white/50">•</span>
            <span className="text-xs text-white/70">
              {currentImageIndex + 1} / {galleryImages.length}
            </span>
          </div>
          <h3 className="text-lg font-semibold mb-1 text-white">
            {galleryImages[currentImageIndex].alt.split(' - ')[0]}
          </h3>
          {galleryImages[currentImageIndex].alt.split(' - ')[1] && (
            <p className="text-sm text-white/80 leading-relaxed">
              {galleryImages[currentImageIndex].alt.split(' - ')[1]}
            </p>
          )}
        </div>
      </div>

      {/* Click outside to close */}
      <div
        className="absolute inset-0 -z-10"
        onClick={closeImageModal}
      />

      {/* Thumbnail navigation */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2 bg-white/20 backdrop-blur-sm rounded-full p-2">
        {galleryImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`w-2 h-2 rounded-full transition-colors duration-200 ${
              index === currentImageIndex ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageModal;